import { Model } from '@/clients/http'

import { OrderHttpParams, OrderResponses } from '@/types'

export class OrderModel extends Model {
  public static readonly correlations = {
    GET_LIST: 'GET_ORDER_LIST',
    GET_GROUPS: 'GET_ORDER_GROUPS',
    GET_BY_ID: 'GET_ORDER_BY_ID',
    CREATE: 'CREATE_ORDER',
    UPDATE_STATUS: 'UPDATE_ORDER_STATUS',
    UPDATE_DISCOUNT: 'UPDATE_ORDER_DISCOUNT',
    UPDATE_CUSTOMER_INFO: 'UPDATE_ORDER_CUSTOMER_INFO',
    UPDATE_ADDRESS: 'UPDATE_ORDER_ADDRESS',
    UPDATE_DELIVERY_INFO: 'UPDATE_ORDER_DELIVERY_INFO',
    ADD_ITEM: 'ADD_ORDER_ITEM',
    UPDATE_ITEM_DISCOUNT: 'UPDATE_ORDER_ITEM_DISCOUNT',
    UPDATE_ITEM_QUANTITY: 'UPDATE_ORDER_ITEM_QUANTITY',
    REMOVE_ITEM: 'REMOVE_ORDER_ITEM',
    ADD_COUPON: 'ADD_ORDER_COUPON',
    REMOVE_COUPON: 'REMOVE_ORDER_COUPON',
    UPDATE_NOTE: 'UPDATE_ORDER_NOTE',
    UPDATE_SOURCE: 'UPDATE_ORDER_SOURCE',
    UPDATE_DELIVERY_LOCATION_TYPE: 'UPDATE_ORDER_DELIVERY_LOCATION_TYPE',
  }

  public static readonly endpoints = {
    getList: (props?: OrderHttpParams.GetList) =>
      this.createEndpoint({
        url: '/api/backoffice/order',
        params: props?.params,
        query: props?.query,
      }),
    getGroups: (props?: OrderHttpParams.GetGroups) =>
      this.createEndpoint({
        url: '/api/backoffice/order/group',
        params: props?.params,
        query: props?.query,
      }),
    getById: (props?: OrderHttpParams.GetById) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id',
        params: props?.params,
        query: props?.query,
      }),
    create: (props?: OrderHttpParams.Create) =>
      this.createEndpoint({
        url: '/api/backoffice/order',
        params: props?.params,
        query: props?.query,
      }),
    updateStatus: (props: OrderHttpParams.UpdateStatus) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/status',
        params: props.params,
        query: props.query,
      }),
    updateDiscount: (props: OrderHttpParams.UpdateDiscount) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/order-discount',
        params: props.params,
        query: props.query,
      }),
    updateCustomerInfo: (props: OrderHttpParams.UpdateCustomerInfo) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/customer-info',
        params: props.params,
        query: props.query,
      }),
    updateAddress: (props: OrderHttpParams.UpdateAddress) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/address',
        params: props.params,
        query: props.query,
      }),
    updateDeliveryInfo: (props: OrderHttpParams.UpdateDeliveryInfo) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/delivery-info',
        params: props.params,
        query: props.query,
      }),
    addItem: (props: OrderHttpParams.AddItem) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/item',
        params: props.params,
        query: props.query,
      }),
    updateItemDiscount: (props: OrderHttpParams.UpdateItemDiscount) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/item/:itemId/discount',
        params: props.params,
        query: props.query,
      }),
    updateItemQuantity: (props: OrderHttpParams.UpdateItemQuantity) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/item/:itemId/quantity',
        params: props.params,
        query: props.query,
      }),
    removeItem: (props: OrderHttpParams.RemoveItem) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/item/:itemId',
        params: props.params,
        query: props.query,
      }),
    addCoupon: (props: OrderHttpParams.AddCoupon) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/add-coupon',
        params: props.params,
        query: props.query,
      }),
    removeCoupon: (props: OrderHttpParams.RemoveCoupon) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/remove-coupon',
        params: props.params,
        query: props.query,
      }),
    updateNote: (props: OrderHttpParams.UpdateNote) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/note',
        params: props.params,
      }),
    updateSource: (props: OrderHttpParams.UpdateSource) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/source',
        params: props.params,
      }),
    updateDeliveryLocationType: (props: OrderHttpParams.UpdateDeliveryLocationType) =>
      this.createEndpoint({
        url: '/api/backoffice/order/:id/delivery-location-type',
        params: props.params,
      }),
  }

  public static readonly getList = async (props?: OrderHttpParams.GetList) => {
    return this.createHttpRequest<OrderResponses.GetList, OrderResponses.GetListError>({
      correlationKey: this.correlations.GET_LIST,
      payload: {
        method: 'get',
        url: this.endpoints.getList(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly getGroups = async (props?: OrderHttpParams.GetGroups) => {
    return this.createHttpRequest<OrderResponses.GetGroups, OrderResponses.GetGroupsError>({
      correlationKey: this.correlations.GET_GROUPS,
      payload: {
        method: 'get',
        url: this.endpoints.getGroups(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly getById = async (props: OrderHttpParams.GetById) => {
    return this.createHttpRequest<OrderResponses.GetById, OrderResponses.GetByIdError>({
      correlationKey: this.correlations.GET_BY_ID,
      payload: {
        method: 'get',
        url: this.endpoints.getById(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly create = async (props: OrderHttpParams.Create) => {
    return this.createHttpRequest<OrderResponses.Create, OrderResponses.CreateError>({
      correlationKey: this.correlations.CREATE,
      payload: {
        method: 'post',
        url: this.endpoints.create(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateStatus = async (props: OrderHttpParams.UpdateStatus) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_STATUS,
      payload: {
        method: 'put',
        url: this.endpoints.updateStatus(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateDiscount = async (props: OrderHttpParams.UpdateDiscount) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_DISCOUNT,
      payload: {
        method: 'put',
        url: this.endpoints.updateDiscount(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateCustomerInfo = async (props: OrderHttpParams.UpdateCustomerInfo) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_CUSTOMER_INFO,
      payload: {
        method: 'put',
        url: this.endpoints.updateCustomerInfo(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateAddress = async (props: OrderHttpParams.UpdateAddress) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_ADDRESS,
      payload: {
        method: 'put',
        url: this.endpoints.updateAddress(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateDeliveryInfo = async (props: OrderHttpParams.UpdateDeliveryInfo) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_DELIVERY_INFO,
      payload: {
        method: 'put',
        url: this.endpoints.updateDeliveryInfo(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly addItem = async (props: OrderHttpParams.AddItem) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.ADD_ITEM,
      payload: {
        method: 'post',
        url: this.endpoints.addItem(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateItemDiscount = async (props: OrderHttpParams.UpdateItemDiscount) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_ITEM_DISCOUNT,
      payload: {
        method: 'put',
        url: this.endpoints.updateItemDiscount(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateItemQuantity = async (props: OrderHttpParams.UpdateItemQuantity) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_ITEM_QUANTITY,
      payload: {
        method: 'put',
        url: this.endpoints.updateItemQuantity(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly removeItem = async (props: OrderHttpParams.RemoveItem) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.REMOVE_ITEM,
      payload: {
        method: 'delete',
        url: this.endpoints.removeItem(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly addCoupon = async (props: OrderHttpParams.AddCoupon) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.ADD_COUPON,
      payload: {
        method: 'put',
        url: this.endpoints.addCoupon(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly removeCoupon = async (props: OrderHttpParams.RemoveCoupon) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.REMOVE_COUPON,
      payload: {
        method: 'put',
        url: this.endpoints.removeCoupon(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateNote = async (props: OrderHttpParams.UpdateNote) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_NOTE,
      payload: {
        method: 'put',
        url: this.endpoints.updateNote(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateSource = async (props: OrderHttpParams.UpdateSource) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_SOURCE,
      payload: {
        method: 'put',
        url: this.endpoints.updateSource(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  public static readonly updateDeliveryLocationType = async (
    props: OrderHttpParams.UpdateDeliveryLocationType
  ) => {
    return this.createHttpRequest<OrderResponses.CommonResponse, OrderResponses.CommonError>({
      correlationKey: this.correlations.UPDATE_DELIVERY_LOCATION_TYPE,
      payload: {
        method: 'put',
        url: this.endpoints.updateDeliveryLocationType(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }
}
